<script setup lang="ts">
interface Props {
    widgetId: string;
    name: string;
    color?: string;
}

const props = withDefaults(defineProps<Props>(), {
    color: '#ffaa00',
});

const {
    sendSpeedDialFactor,
    sendSpeedDialTime,
    sendSpeedDialStep,
    triggerHaptic,
    qlcStore,
} = useQlcPlus();

interface MultiplierOption {
    label: string;
    index: number;
    factor: number;
}

// QLC+ Standard Multipliers / Dividers
// Indices match QLC+ internal VCSpeedDialFunction::Multiplier enum:
// 2: 1/16, 3: 1/8, 4: 1/4, 5: 1/2, 6: 1, 7: 2, 8: 4, 9: 8, 10: 16
const multipliers: MultiplierOption[] = [
    { label: '1/16', index: 2, factor: 1 / 16 },
    { label: '1/8', index: 3, factor: 1 / 8 },
    { label: '1/4', index: 4, factor: 1 / 4 },
    { label: '1/2', index: 5, factor: 1 / 2 },
    { label: '1', index: 6, factor: 1 },
    { label: '2', index: 7, factor: 2 },
    { label: '4', index: 8, factor: 4 },
    { label: '8', index: 9, factor: 8 },
    { label: '16', index: 10, factor: 16 },
];

// Current index in multipliers array (default index 4 = label '1', index 6)
const activeMultiplierIndex = ref(4);

// Base tempo in BPM and interval in milliseconds
const bpm = ref(120);
const baseIntervalMs = ref(500);

// History of recent tap timestamps
const tapHistory = ref<number[]>([]);
const isTapFlashing = ref(false);
const isBeatPulse = ref(false);

let beatTimer: ReturnType<typeof setInterval> | null = null;
let tapTimeoutTimer: ReturnType<typeof setTimeout> | null = null;

const currentMultiplier = computed(() => {
    return multipliers[activeMultiplierIndex.value] || multipliers[4]!;
});

// Effective duration taking the multiplier into account
const effectiveMs = computed(() => {
    return Math.max(1, Math.round(baseIntervalMs.value * currentMultiplier.value.factor));
});

// Tap Tempo logic: calculates interval in ms and sends: <widgetId>|SPEED_TIME|<ms>
function handleTap(): void {
    const now = Date.now();
    triggerHaptic(30);

    // Visual tap flash
    isTapFlashing.value = true;
    if (tapTimeoutTimer) {
        clearTimeout(tapTimeoutTimer);
    }
    tapTimeoutTimer = setTimeout(() => {
        isTapFlashing.value = false;
    }, 120);

    // Clean history if last tap was longer than 2.5 seconds ago
    if (tapHistory.value.length > 0) {
        const lastTap = tapHistory.value[tapHistory.value.length - 1];
        if (lastTap && now - lastTap > 2500) {
            tapHistory.value = [];
        }
    }

    tapHistory.value.push(now);

    // Keep maximum last 8 taps
    if (tapHistory.value.length > 8) {
        tapHistory.value.shift();
    }

    // Calculate tempo if we have 2 or more taps
    if (tapHistory.value.length >= 2) {
        const intervals: number[] = [];
        for (let i = 1; i < tapHistory.value.length; i++) {
            const current = tapHistory.value[i];
            const prev = tapHistory.value[i - 1];
            if (current !== undefined && prev !== undefined) {
                intervals.push(current - prev);
            }
        }

        const avgInterval = intervals.reduce((sum, val) => sum + val, 0) / intervals.length;
        const clampedInterval = Math.max(50, Math.min(10000, avgInterval));
        const roundedMs = Math.round(clampedInterval);

        baseIntervalMs.value = roundedMs;
        bpm.value = Math.round(60000 / roundedMs);

        restartBeatAnimation();
        sendSpeedDialTime(props.widgetId, roundedMs);
    }
}

// Decrease multiplier (halve: e.g. from 1 to 1/2) -> sends: <widgetId>|SPEED_DOWN
function handleDecreaseMultiplier(): void {
    if (activeMultiplierIndex.value > 0) {
        triggerHaptic(20);
        activeMultiplierIndex.value--;
        sendSpeedDialStep(props.widgetId, 'down');
    }
}

// Increase multiplier (double: e.g. from 1 to 2) -> sends: <widgetId>|SPEED_UP
function handleIncreaseMultiplier(): void {
    if (activeMultiplierIndex.value < multipliers.length - 1) {
        triggerHaptic(20);
        activeMultiplierIndex.value++;
        sendSpeedDialStep(props.widgetId, 'up');
    }
}

// Select a specific multiplier directly -> sends: <widgetId>|SPEED_FACTOR|<factorIndex>
function handleSelectMultiplier(index: number): void {
    if (index < 0 || index >= multipliers.length) {
        return;
    }
    triggerHaptic(15);
    activeMultiplierIndex.value = index;
    const item = multipliers[index];
    if (item) {
        sendSpeedDialFactor(props.widgetId, item.index);
    }
}

// Adjust BPM by +/- 1 -> sends: <widgetId>|SPEED_TIME|<ms>
function adjustBpm(delta: number): void {
    triggerHaptic(15);
    const newBpm = Math.max(20, Math.min(400, bpm.value + delta));
    bpm.value = newBpm;
    const newMs = Math.round(60000 / newBpm);
    baseIntervalMs.value = newMs;
    restartBeatAnimation();
    sendSpeedDialTime(props.widgetId, newMs);
}

// Reset multiplier back to 1x -> sends: <widgetId>|SPEED_FACTOR|6
function handleResetMultiplier(): void {
    triggerHaptic(20);
    activeMultiplierIndex.value = 4; // index of '1'
    sendSpeedDialFactor(props.widgetId, 6);
}

// Metronome beat pulse animation locked to the base tempo (BPM)
function restartBeatAnimation(): void {
    if (beatTimer) {
        clearInterval(beatTimer);
    }
    const interval = Math.max(100, Math.min(2000, baseIntervalMs.value));
    beatTimer = setInterval(() => {
        isBeatPulse.value = true;
        setTimeout(() => {
            isBeatPulse.value = false;
        }, 80);
    }, interval);
}

// Watch for external SpeedDial updates from WebSocket
const speedDialState = computed(() => qlcStore.speedDialStates[props.widgetId]);

watch(
    () => speedDialState.value?.factorIndex,
    (newFactorIndex) => {
        if (newFactorIndex !== undefined) {
            const foundIdx = multipliers.findIndex(m => m.index === newFactorIndex);
            if (foundIdx !== -1 && foundIdx !== activeMultiplierIndex.value) {
                activeMultiplierIndex.value = foundIdx;
            }
        }
    },
);

watch(
    () => speedDialState.value?.bpm,
    (newBpm) => {
        if (newBpm !== undefined && newBpm > 0 && Math.abs(newBpm - bpm.value) >= 1) {
            bpm.value = newBpm;
            baseIntervalMs.value = Math.round(60000 / newBpm);
            restartBeatAnimation();
        }
    },
);

watch(
    () => speedDialState.value?.timeMs,
    (newTimeMs) => {
        if (newTimeMs !== undefined && newTimeMs > 0 && Math.abs(newTimeMs - baseIntervalMs.value) > 5) {
            baseIntervalMs.value = newTimeMs;
            bpm.value = Math.round(60000 / newTimeMs);
            restartBeatAnimation();
        }
    },
);

onMounted(() => {
    const existingState = qlcStore.speedDialStates[props.widgetId];
    if (existingState) {
        if (existingState.bpm && existingState.bpm > 0) {
            bpm.value = existingState.bpm;
            baseIntervalMs.value = existingState.timeMs || Math.round(60000 / existingState.bpm);
        }
        else if (existingState.timeMs > 0) {
            baseIntervalMs.value = existingState.timeMs;
            bpm.value = Math.round(60000 / existingState.timeMs);
        }
        if (existingState.factorIndex) {
            const foundIdx = multipliers.findIndex(m => m.index === existingState.factorIndex);
            if (foundIdx !== -1) {
                activeMultiplierIndex.value = foundIdx;
            }
        }
    }
    else {
        const widget = qlcStore.widgets[props.widgetId];
        if (widget && widget.value > 0) {
            baseIntervalMs.value = widget.value;
            bpm.value = Math.round(60000 / widget.value);
        }
    }
    restartBeatAnimation();
});

onUnmounted(() => {
    if (beatTimer) {
        clearInterval(beatTimer);
    }
    if (tapTimeoutTimer) {
        clearTimeout(tapTimeoutTimer);
    }
});
</script>

<template>
    <div
        class="speed-dial-card"
        :style="{
            '--speed-accent': color,
        }"
    >
        <!-- Header -->
        <div class="speed-header">
            <div class="header-left">
                <v-icon
                    class="header-icon"
                    icon="mdi-metronome"
                />
                <span class="speed-title">{{ name }}</span>
            </div>
            <div class="header-right">
                <span
                    class="beat-dot"
                    :class="{ 'beat-dot--pulse': isBeatPulse }"
                />
                <span class="speed-badge">SPEED DIAL</span>
            </div>
        </div>

        <!-- BPM & Duration Main Display -->
        <div class="display-panel">
            <div class="bpm-block">
                <div class="bpm-row">
                    <span class="bpm-number">{{ bpm }}</span>
                    <span class="bpm-unit">BPM</span>
                </div>
                <div class="bpm-fine-tune">
                    <button
                        type="button"
                        class="tune-btn"
                        title="-1 BPM"
                        @click="adjustBpm(-1)"
                    >
                        -1
                    </button>
                    <button
                        type="button"
                        class="tune-btn"
                        title="+1 BPM"
                        @click="adjustBpm(1)"
                    >
                        +1
                    </button>
                </div>
            </div>

            <div class="time-block">
                <span class="time-val">{{ baseIntervalMs }} ms</span>
                <span class="factor-badge">
                    {{ currentMultiplier.label }}x
                    <span
                        v-if="currentMultiplier.label !== '1'"
                        class="base-ms-sub"
                    >({{ effectiveMs }}ms eff.)</span>
                </span>
            </div>
        </div>

        <!-- Tap Button & Halve/Double Stepper -->
        <div class="tap-section">
            <!-- Minus Button: Halve multiplier -->
            <button
                type="button"
                class="stepper-btn stepper-btn--minus"
                :disabled="activeMultiplierIndex === 0"
                title="Halveer verdeler (/2)"
                @click="handleDecreaseMultiplier"
            >
                <v-icon icon="mdi-minus" />
                <span class="stepper-sub">÷ 2</span>
            </button>

            <!-- Huge Tap Button with visual beat ripple -->
            <button
                type="button"
                class="tap-btn"
                :class="{
                    'tap-btn--flash': isTapFlashing,
                    'tap-btn--pulse': isBeatPulse,
                }"
                @click="handleTap"
            >
                <v-icon
                    class="tap-icon"
                    icon="mdi-gesture-tap"
                />
                <span class="tap-text">TAP TEMPO</span>
                <span class="tap-hint">TIK OP DE BEAT</span>
            </button>

            <!-- Plus Button: Double multiplier -->
            <button
                type="button"
                class="stepper-btn stepper-btn--plus"
                :disabled="activeMultiplierIndex === multipliers.length - 1"
                title="Verdubbel verdeler (x2)"
                @click="handleIncreaseMultiplier"
            >
                <v-icon icon="mdi-plus" />
                <span class="stepper-sub">× 2</span>
            </button>
        </div>

        <!-- Multipliers / Dividers Row (1/16 to 16) -->
        <div class="multipliers-container">
            <div class="multipliers-header">
                <span class="multipliers-label">VERDELER / FACTOR</span>
                <button
                    v-if="currentMultiplier.label !== '1'"
                    type="button"
                    class="reset-factor-btn"
                    @click="handleResetMultiplier"
                >
                    RESET 1x
                </button>
            </div>

            <div class="multipliers-grid">
                <button
                    v-for="(item, idx) in multipliers"
                    :key="item.label"
                    type="button"
                    class="mult-pill"
                    :class="{ 'mult-pill--active': activeMultiplierIndex === idx }"
                    @click="handleSelectMultiplier(idx)"
                >
                    {{ item.label }}
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped>
.speed-dial-card {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 14px;
    background: #141424;
    border: 2px solid var(--speed-accent);
    border-radius: 18px;
    box-shadow: 0 4px 18px rgba(0, 0, 0, 0.45), 0 0 14px rgba(255, 170, 0, 0.2);
}

.speed-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.header-left {
    display: flex;
    align-items: center;
    gap: 8px;
}

.header-icon {
    color: var(--speed-accent);
    font-size: 22px;
}

.speed-title {
    font-size: 14px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #ffffff;
}

.header-right {
    display: flex;
    align-items: center;
    gap: 8px;
}

.beat-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.4);
    transition: all 0.08s ease;
}

.beat-dot--pulse {
    background: var(--speed-accent);
    box-shadow: 0 0 10px var(--speed-accent);
    transform: scale(1.4);
}

.speed-badge {
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 1px;
    padding: 2px 7px;
    border-radius: 6px;
    background: rgba(255, 170, 0, 0.18);
    color: #ffaa00;
    border: 1px solid rgba(255, 170, 0, 0.3);
}

.display-panel {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    background: #0d0d18;
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 12px;
    margin-bottom: 12px;
}

.bpm-block {
    display: flex;
    align-items: baseline;
    gap: 12px;
}

.bpm-row {
    display: flex;
    align-items: baseline;
    gap: 5px;
}

.bpm-number {
    font-size: 32px;
    font-weight: 900;
    line-height: 1;
    color: #ffffff;
    font-family: monospace;
    letter-spacing: -1px;
}

.bpm-unit {
    font-size: 13px;
    font-weight: 800;
    color: var(--speed-accent);
}

.bpm-fine-tune {
    display: flex;
    gap: 4px;
}

.tune-btn {
    padding: 2px 7px;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 5px;
    color: rgba(255, 255, 255, 0.7);
    font-size: 10px;
    font-weight: 800;
    cursor: pointer;
}

.tune-btn:active {
    background: rgba(255, 255, 255, 0.2);
    color: #ffffff;
}

.time-block {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 3px;
}

.time-val {
    font-size: 15px;
    font-weight: 800;
    color: #ffffff;
    font-family: monospace;
}

.factor-badge {
    font-size: 11px;
    font-weight: 800;
    color: var(--speed-accent);
    background: rgba(255, 170, 0, 0.12);
    padding: 1px 6px;
    border-radius: 5px;
}

.base-ms-sub {
    font-size: 10px;
    color: rgba(255, 255, 255, 0.5);
    margin-left: 3px;
}

.tap-section {
    display: flex;
    align-items: stretch;
    gap: 8px;
    margin-bottom: 12px;
    height: 72px;
}

.stepper-btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 58px;
    background: #1b1b2d;
    border: 1px solid rgba(255, 255, 255, 0.18);
    border-radius: 12px;
    color: #ffffff;
    cursor: pointer;
    transition: all 0.12s ease;
}

.stepper-btn:active:not(:disabled) {
    transform: scale(0.95);
    background: rgba(255, 255, 255, 0.15);
}

.stepper-btn:disabled {
    opacity: 0.35;
    cursor: not-allowed;
}

.stepper-sub {
    font-size: 10px;
    font-weight: 800;
    color: var(--speed-accent);
    margin-top: -2px;
}

.tap-btn {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, rgba(255, 170, 0, 0.22), rgba(255, 85, 0, 0.28));
    border: 2px solid var(--speed-accent);
    border-radius: 14px;
    color: #ffffff;
    cursor: pointer;
    user-select: none;
    touch-action: manipulation;
    transition: all 0.1s ease;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.3);
}

.tap-btn:active,
.tap-btn--flash {
    background: var(--speed-accent) !important;
    color: #000000 !important;
    transform: scale(0.97);
    box-shadow: 0 0 20px var(--speed-accent) !important;
}

.tap-btn--flash .tap-icon,
.tap-btn--flash .tap-text,
.tap-btn--flash .tap-hint {
    color: #000000 !important;
}

.tap-icon {
    font-size: 20px;
    color: var(--speed-accent);
    margin-bottom: 1px;
}

.tap-text {
    font-size: 15px;
    font-weight: 900;
    letter-spacing: 1px;
    line-height: 1.1;
}

.tap-hint {
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.5px;
    color: rgba(255, 255, 255, 0.6);
}

.multipliers-container {
    display: flex;
    flex-direction: column;
}

.multipliers-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;
}

.multipliers-label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.5px;
    color: rgba(255, 255, 255, 0.6);
}

.reset-factor-btn {
    font-size: 10px;
    font-weight: 800;
    color: var(--speed-accent);
    background: rgba(255, 170, 0, 0.12);
    border: 1px solid rgba(255, 170, 0, 0.3);
    padding: 1px 6px;
    border-radius: 5px;
    cursor: pointer;
}

.multipliers-grid {
    display: grid;
    grid-template-columns: repeat(9, 1fr);
    gap: 4px;
}

.mult-pill {
    padding: 7px 2px;
    background: #18182b;
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 7px;
    color: rgba(255, 255, 255, 0.7);
    font-size: 10px;
    font-weight: 800;
    cursor: pointer;
    text-align: center;
    transition: all 0.12s ease;
}

.mult-pill:active {
    transform: scale(0.95);
}

.mult-pill--active {
    background: var(--speed-accent);
    border-color: #ffffff;
    color: #000000;
    font-weight: 900;
    box-shadow: 0 0 10px var(--speed-accent);
}
</style>
